import { useCallback, useEffect, useState } from 'react';
import {
  tabAnalyzerPromptsApi,
  type TestPromptInput,
} from '@/services/tab-analyzer-prompts-api';
import type { IOverviewHighlight, ITabAnalyzerPrompt, TabId } from '@skystone/shared';

interface UseTabAnalyzerPromptsResult {
  prompts: ITabAnalyzerPrompt[];
  isLoading: boolean;
  error: string | null;
  /** Update a single tab's prompt; returns the updated row on success. */
  updatePrompt: (tabId: TabId, systemPrompt: string) => Promise<ITabAnalyzerPrompt>;
  /** Dry-run the supplied prompt against the live data; nothing is persisted. */
  testPrompt: (tabId: TabId, input: TestPromptInput) => Promise<IOverviewHighlight[] | null>;
  refetch: () => void;
}

/**
 * Read + mutate hook for the admin "AI Analyzers" surface (PRODOPSAI-407).
 *
 * Loads on mount; exposes optimistic updates that swap the local list when
 * the API call succeeds. Errors bubble up so the UI can decide between inline
 * + toast surfaces.
 */
export function useTabAnalyzerPrompts(): UseTabAnalyzerPromptsResult {
  const [prompts, setPrompts] = useState<ITabAnalyzerPrompt[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    tabAnalyzerPromptsApi
      .list()
      .then((data) => {
        if (cancelled) return;
        setPrompts(data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load tab analyzer prompts');
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const updatePrompt = useCallback(
    async (tabId: TabId, systemPrompt: string): Promise<ITabAnalyzerPrompt> => {
      const updated = await tabAnalyzerPromptsApi.update(tabId, systemPrompt);
      setPrompts((prev) => prev.map((row) => (row.tabId === tabId ? updated : row)));
      return updated;
    },
    [],
  );

  const testPrompt = useCallback(
    async (tabId: TabId, input: TestPromptInput): Promise<IOverviewHighlight[] | null> => {
      const result = await tabAnalyzerPromptsApi.test(tabId, input);
      return result.insights;
    },
    [],
  );

  const refetch = useCallback(() => {
    setReloadKey((k) => k + 1);
  }, []);

  return { prompts, isLoading, error, updatePrompt, testPrompt, refetch };
}
